import { useEffect, useEffectEvent } from 'react';
import useAuth from '@/auth/application/useAuth';
import { useGpsPositionUpdates } from '@/map/presentation/hooks/useGpsPositionUpdates';

/**
 * Provider component to initialize GPS SignalR connection on app login
 * This component should be placed inside AuthProvider to ensure user context is available
 */
const signalRDisabled = import.meta.env.VITE_DISABLE_SIGNALR === 'true';

export const GpsSignalRProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isLoggedIn } = useAuth();
  const shouldEnableSignalR = isLoggedIn && !signalRDisabled;

  // Initialize GPS SignalR connection when user is logged in
  const { isConnected, error, connectionState } = useGpsPositionUpdates(shouldEnableSignalR);

  // Stable logging callback using useEffectEvent - doesn't cause re-renders
  const logConnectionStatus = useEffectEvent(() => {
    if (shouldEnableSignalR) {
      if (isConnected) {
        console.log('GPS SignalR connected successfully');
      } else if (error) {
        console.warn('GPS SignalR connection error:', error);
      } else if (connectionState.isReconnecting) {
        console.log('GPS SignalR reconnecting...');
      }
    }
  });

  // Log connection status changes
  useEffect(() => {
    logConnectionStatus();
  }, [shouldEnableSignalR, isConnected, error, connectionState.isReconnecting]);

  return <>{children}</>;
};
